// Nectar Trail — a bee-themed TriPeaks solitaire journey.
//
// The bee travels a long dotted trail of flowers (levels). Each level is a game
// of TriPeaks: clear the three flower "peaks" by tapping any exposed flower whose
// nectar number is one higher or lower than the current bloom (with 13↔1 wrap).
// Games are timed — every second left when you clear the board becomes a honey
// drop carried home to grow the hive. The bee can't move to the next flower until
// the current one is cleared. Every few flowers is a GOAL bloom that grants a
// power-up. Reach the hive at the end of the trail with as much honey as you can.

export const TOTAL_LEVELS = 200;
export const GOAL_EVERY = 8; // every Nth level is a goal (power-up) bloom
export const isGoalLevel = (lvl) => lvl > 0 && lvl % GOAL_EVERY === 0;

export const SUIT_FLOWER = ["🌼", "🌸", "🌺", "🌷"];
export const SUIT_COLOR = ["#F59E0B", "#EC4899", "#EF4444", "#A855F7"];
const TRAIL_FLOWERS = ["🌼", "🌸", "🌺", "🌷", "🪻", "🌻", "💐", "🏵️"];

// Board geometry (28 cards). POS[i] = [row, x] where x is in half-card units.
export const POS = [
  [0, 3], [0, 9], [0, 15],
  [1, 2], [1, 4], [1, 8], [1, 10], [1, 14], [1, 16],
  [2, 1], [2, 3], [2, 5], [2, 7], [2, 9], [2, 11], [2, 13], [2, 15], [2, 17],
  [3, 0], [3, 2], [3, 4], [3, 6], [3, 8], [3, 10], [3, 12], [3, 14], [3, 16], [3, 18],
];
// COVERS[i] = the two cards that sit on top of card i (it's exposed when both are gone).
const COVERS = [
  [3, 4], [5, 6], [7, 8],
  [9, 10], [10, 11], [12, 13], [13, 14], [15, 16], [16, 17],
  [18, 19], [19, 20], [20, 21], [21, 22], [22, 23], [23, 24], [24, 25], [25, 26], [26, 27],
  [], [], [], [], [], [], [], [], [], [],
];
export const BOARD_UNITS_W = 20; // 0..18 plus a 2-unit card width

export const powerTypes = ["time", "wild", "draw", "undo"];
export const POWER_META = {
  time: { icon: "⏱️", label: "Time +20s", desc: "Add 20 seconds to the clock." },
  wild: { icon: "🌈", label: "Wild Bloom", desc: "Next flower plays no matter its number." },
  draw: { icon: "🍃", label: "Extra Nectar", desc: "Add 3 cards to the draw pile." },
  undo: { icon: "↩️", label: "Backtrack", desc: "Undo your last move." },
};

function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function shuffle(arr, rng) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function levelConfig(level) {
  const time = Math.max(45, 90 - Math.floor(level / 5)); // slowly tighter clock
  const draws = Math.max(14, 23 - Math.floor(level / 25)); // fewer draws later
  return { time, draws, wrap: true, isGoal: isGoalLevel(level) };
}

// Which power-up a goal bloom grants (rotates through the set).
export function goalReward(level) {
  return powerTypes[(Math.floor(level / GOAL_EVERY) - 1 + powerTypes.length) % powerTypes.length];
}

export function flowerForLevel(level) {
  return TRAIL_FLOWERS[Math.floor((level - 1) / GOAL_EVERY) % TRAIL_FLOWERS.length];
}

function buildDeal(level, seed) {
  const cfg = levelConfig(level);
  const rng = mulberry32(seed);
  const deck = [];
  for (let r = 1; r <= 13; r++) for (let s = 0; s < 4; s++) deck.push({ rank: r, suit: s });
  shuffle(deck, rng);
  const cards = POS.map((p, i) => ({ id: i, rank: deck[i].rank, suit: deck[i].suit, row: p[0], x: p[1], removed: false }));
  const pool = deck.slice(28, 28 + cfg.draws + 1).map((c) => ({ ...c }));
  return { level, cards, stock: pool.slice(1), waste: [pool[0]], wrap: cfg.wrap, timeLimit: cfg.time, isGoal: cfg.isGoal, won: false, moves: 0 };
}

// Does a clearing line exist for this deal? A memoised DFS over (removed cards,
// draws used, current rank) — the state space is a DAG (both only grow), so a
// visited set both prunes and prevents cycles. Solvable deals return true quickly
// (first solution wins); if the search exhausts `budget` without proof we treat
// the deal as unusable (return false) so only *proven* deals are ever served.
function hasSolution(deal) {
  const ranks = deal.cards.map((c) => c.rank);
  const stock = deal.stock;
  const FULL = (1 << 28) - 1;
  const memo = new Set();
  let budget = 120000;
  const exposedMask = (mask, i) => COVERS[i].every((j) => (mask & (1 << j)) !== 0);
  const dfs = (mask, stockIdx, cur) => {
    if (mask === FULL) return true;
    if (budget-- <= 0) return false;
    const key = mask + "_" + stockIdx + "_" + cur;
    if (memo.has(key)) return false;
    memo.add(key);
    for (let i = 0; i < 28; i++) {
      if (mask & (1 << i)) continue;
      if (!exposedMask(mask, i)) continue;
      const d = Math.abs(ranks[i] - cur);
      if ((d === 1 || (deal.wrap && d === 12)) && dfs(mask | (1 << i), stockIdx, ranks[i])) return true;
    }
    if (stockIdx < stock.length && dfs(mask, stockIdx + 1, stock[stock.length - 1 - stockIdx].rank)) return true;
    return false;
  };
  return dfs(0, 0, deal.waste[0].rank);
}

// Build a fresh, winnable TriPeaks game. `attempt` reshuffles on retry, and each
// candidate deal is solver-checked so the bee is never handed a dead deal.
export function newGame(level, attempt = 0) {
  let last = null;
  for (let k = 0; k < 40; k++) {
    const seed = (Math.imul(level, 2654435761) ^ Math.imul(attempt * 40 + k + 1, 40503) ^ 0x9e3779b9) >>> 0;
    last = buildDeal(level, seed);
    if (hasSolution(last)) break;
  }
  return { ...last, attempt };
}

export const exposed = (cards, i) => COVERS[i].every((j) => cards[j].removed);
const adj = (a, b, wrap) => {
  const d = Math.abs(a - b);
  return d === 1 || (wrap && d === 12);
};
export const current = (state) => state.waste[state.waste.length - 1];

export function playableIds(state, wild = false) {
  const cur = current(state);
  const out = [];
  for (const c of state.cards) {
    if (c.removed || !exposed(state.cards, c.id)) continue;
    if (wild || adj(c.rank, cur.rank, state.wrap)) out.push(c.id);
  }
  return out;
}

export function play(state, id, wild = false) {
  const card = state.cards.find((c) => c.id === id);
  if (!card || card.removed) return { ok: false };
  if (!exposed(state.cards, id)) return { ok: false, note: "That flower is still covered" };
  if (!wild && !adj(card.rank, current(state).rank, state.wrap)) return { ok: false, note: "Play one higher or lower" };
  const cards = state.cards.map((c) => (c.id === id ? { ...c, removed: true } : c));
  const waste = [...state.waste, { rank: card.rank, suit: card.suit }];
  const won = cards.every((c) => c.removed);
  return { ok: true, state: { ...state, cards, waste, moves: state.moves + 1, won } };
}

export function draw(state) {
  if (!state.stock.length) return { ok: false, note: "No nectar left to draw" };
  const stock = state.stock.slice();
  const c = stock.pop();
  return { ok: true, state: { ...state, stock, waste: [...state.waste, c], moves: state.moves + 1 } };
}

// Power-up: add n bonus cards to the draw pile.
export function addDraws(state, n = 3) {
  const extra = [];
  for (let i = 0; i < n; i++) extra.push({ rank: 1 + Math.floor(Math.random() * 13), suit: Math.floor(Math.random() * 4) });
  return { ...state, stock: [...state.stock, ...extra] };
}

// No cards left to draw and nothing playable.
export function isStuck(state) {
  if (state.won || state.stock.length > 0) return false;
  return playableIds(state).length === 0;
}

export function sanitizeProgress(p) {
  const pu = {};
  for (const t of powerTypes) pu[t] = Math.max(0, Math.min(999, (p && p.pu && p.pu[t]) | 0));
  return {
    level: Math.max(1, Math.min(TOTAL_LEVELS + 1, (p && p.level) | 0 || 1)),
    honey: Math.max(0, Math.min(1e12, (p && p.honey) | 0)),
    pu,
  };
}
